import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { LEAGUES, getTeamById, searchTeams } from '../data/teams';
import { applyAppearance, dispatchAppearance } from '../theme';
import './Profile.css';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3002';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const Profile = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [message, setMessage] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [inputError, setInputError] = useState('');
  const [theme, setTheme] = useState(localStorage.getItem('theme') === 'dark' ? 'dark' : 'light');
  const [favoriteTeams, setFavoriteTeams] = useState([]);
  const [spearheadTeamId, setSpearheadTeamId] = useState('');
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [leagueTab, setLeagueTab] = useState('ncaa');
  const [teamQuery, setTeamQuery] = useState('');
  const navigate = useNavigate();

  const validateInput = (input) => {
    if (/\s/.test(input)) {
      setInputError('Spaces are not allowed in usernames. Please use letters, numbers, or dashes.');
      return false;
    }
    const sanitized = input.replace(/[^a-zA-Z0-9-]/g, '');
    if (sanitized !== input) {
      setInputError('Please use only letters, numbers, or dashes for your new name.');
      return false;
    }
    setInputError('');
    return sanitized;
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get(`${apiUrl}/api/auth/verify`, { headers: authHeaders() })
      .then((response) => {
        const user = response.data.user || {};
        setUsername(user.name);
        setEmail(user.email || 'Email not available');
        const nextTheme = user.theme === 'dark' ? 'dark' : 'light';
        const nextFavorites = Array.isArray(user.favoriteTeams) ? user.favoriteTeams : [];
        const nextSpearhead = user.spearheadTeamId || (nextFavorites.length === 1 ? nextFavorites[0].id : '');
        setTheme(nextTheme);
        setFavoriteTeams(nextFavorites);
        setSpearheadTeamId(nextSpearhead);
        applyAppearance({ theme: nextTheme, team: getTeamById(nextSpearhead) });
      })
      .catch(() => {
        setMessage('Failed to load profile');
      });
  }, []);

  const handleSignOff = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('isAdmin');
    applyAppearance({ theme, team: null });
    dispatchAppearance({ theme, spearheadTeamId: '' });
    window.dispatchEvent(new Event('auth-signed-off'));
    setUsername('');
    navigate('/');
  };

  const handleUpdateUsername = async () => {
    const validatedUsername = validateInput(newUsername);
    if (!validatedUsername) return;
    if (validatedUsername.length < 3) {
      setMessage('Username must be at least 3 characters long');
      return;
    }
    if (validatedUsername.length > 30) {
      setMessage('Username cannot exceed 30 characters');
      return;
    }

    try {
      await axios.put(
        `${apiUrl}/api/auth/update-username`,
        { name: validatedUsername },
        { headers: authHeaders() }
      );
      setUsername(validatedUsername);
      setNewUsername('');
      setIsEditing(false);
      setMessage('Username updated successfully. Name changes are limited to once every 90 days.');
    } catch (error) {
      if (error.response?.data?.error === 'Username already exists') {
        setMessage('This username is already taken. Please choose another.');
      } else if (error.response?.data?.error === 'Name change rate limit exceeded') {
        setMessage('Name changes are limited to once every 90 days. Please try again later.');
      } else if (error.response?.data?.error === 'Profanity detected') {
        setMessage('Username contains inappropriate content. Please choose another.');
      } else if (error.response?.data?.error === 'Spaces not allowed') {
        setMessage('Spaces are not allowed in usernames. Please choose another.');
      } else {
        setMessage('Failed to update username: ' + (error.response?.data?.error || 'Unknown error'));
      }
    }
  };

  const savePreferences = async (next) => {
    const payload = {
      theme: next.theme,
      favoriteTeams: next.favoriteTeams,
      spearheadTeamId: next.spearheadTeamId,
    };
    setSavingPrefs(true);
    try {
      const res = await axios.put(`${apiUrl}/api/auth/preferences`, payload, { headers: authHeaders() });
      const user = res.data.user || payload;
      const savedTheme = user.theme === 'dark' ? 'dark' : 'light';
      const savedFavorites = Array.isArray(user.favoriteTeams) ? user.favoriteTeams : payload.favoriteTeams;
      const savedSpearhead = user.spearheadTeamId || (savedFavorites.length === 1 ? savedFavorites[0].id : '');
      setTheme(savedTheme);
      setFavoriteTeams(savedFavorites);
      setSpearheadTeamId(savedSpearhead);
      applyAppearance({ theme: savedTheme, team: getTeamById(savedSpearhead) });
      dispatchAppearance({ theme: savedTheme, spearheadTeamId: savedSpearhead, favoriteTeams: savedFavorites });
    } catch (error) {
      setMessage('Failed to save profile look: ' + (error.response?.data?.error || 'Unknown error'));
    } finally {
      setSavingPrefs(false);
    }
  };

  const handleThemeChange = (nextTheme) => {
    setTheme(nextTheme);
    savePreferences({
      theme: nextTheme,
      favoriteTeams,
      spearheadTeamId,
    });
  };

  const addTeam = (team) => {
    if (favoriteTeams.some((item) => item.id === team.id)) return;
    if (favoriteTeams.length >= 12) {
      setMessage('You can save up to 12 favorite teams.');
      return;
    }
    const nextFavorites = [...favoriteTeams, { id: team.id, league: team.league }];
    const nextSpearhead = nextFavorites.length === 1 ? team.id : spearheadTeamId;
    setFavoriteTeams(nextFavorites);
    setSpearheadTeamId(nextSpearhead);
    setTeamQuery('');
    savePreferences({
      theme,
      favoriteTeams: nextFavorites,
      spearheadTeamId: nextSpearhead,
    });
  };

  const removeTeam = (teamId) => {
    const nextFavorites = favoriteTeams.filter((item) => item.id !== teamId);
    const nextSpearhead = nextFavorites.length === 1
      ? nextFavorites[0].id
      : (spearheadTeamId === teamId ? '' : spearheadTeamId);
    setFavoriteTeams(nextFavorites);
    setSpearheadTeamId(nextSpearhead);
    savePreferences({
      theme,
      favoriteTeams: nextFavorites,
      spearheadTeamId: nextSpearhead,
    });
  };

  const setSpearhead = (teamId) => {
    setSpearheadTeamId(teamId);
    savePreferences({
      theme,
      favoriteTeams,
      spearheadTeamId: teamId,
    });
  };

  const searchResults = useMemo(() => {
    const selected = new Set(favoriteTeams.map((item) => item.id));
    return searchTeams(leagueTab, teamQuery).filter((item) => !selected.has(item.id)).slice(0, 8);
  }, [leagueTab, teamQuery, favoriteTeams]);

  const selectedDetails = favoriteTeams
    .map((item) => getTeamById(item.id))
    .filter(Boolean);
  const spearhead = getTeamById(spearheadTeamId);

  return (
    <div className="profile-page">
      <header className="profile-hero">
        <p className="profile-kicker">Your locker</p>
        <h1>My Profile</h1>
        <p className="profile-lead">
          Set the look of MyTop25, pick your teams, and promote one spearhead to color the whole site.
        </p>
      </header>

      {message && <p className="profile-banner">{message}</p>}

      <section className="profile-card">
        <h2>Account</h2>
        <p className="profile-meta"><span>Email</span> {email}</p>
        <p className="profile-meta"><span>Name</span> {username}</p>
        {isEditing ? (
          <>
            <input
              type="text"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              placeholder="Enter new username"
            />
            {inputError && <p className="warning-text">{inputError}</p>}
            <div className="profile-actions">
              <button type="button" onClick={handleUpdateUsername}>Update Name</button>
              <button type="button" className="profile-secondary" onClick={() => setIsEditing(false)}>Cancel</button>
            </div>
          </>
        ) : (
          <div className="profile-actions">
            <button type="button" onClick={() => { setIsEditing(true); setMessage('Name changes are limited to once every 90 days.'); }}>
              Change Name
            </button>
            <button type="button" className="profile-secondary" onClick={handleSignOff}>
              Sign Off MyTop25.com
            </button>
          </div>
        )}
      </section>

      <section className="profile-card">
        <h2>Site look</h2>
        <p className="profile-help">Dark mode is saved to your account and comes back whenever you sign on.</p>
        <div className="theme-toggle" role="group" aria-label="Color theme">
          <button
            type="button"
            className={theme === 'light' ? 'is-active' : ''}
            onClick={() => handleThemeChange('light')}
            disabled={savingPrefs}
          >
            Light
          </button>
          <button
            type="button"
            className={theme === 'dark' ? 'is-active' : ''}
            onClick={() => handleThemeChange('dark')}
            disabled={savingPrefs}
          >
            Dark
          </button>
        </div>
        {spearhead && (
          <p className="profile-spearhead-note">
            Spearhead: <strong>{spearhead.name}</strong>. Soft tints, buttons, and header bars use those team colors.
          </p>
        )}
      </section>

      <section className="profile-card">
        <h2>Favorite teams</h2>
        <p className="profile-help">
          Add NCAA, NFL, MLB, and NBA teams. Promote one as your spearhead. If you only pick one team, that team is used automatically.
        </p>

        {selectedDetails.length > 0 && (
          <ul className="favorite-chips">
            {selectedDetails.map((team) => {
              const isSpearhead = team.id === spearheadTeamId;
              return (
                <li key={team.id} className={isSpearhead ? 'is-spearhead' : ''}>
                  <span className="team-swatch" style={{ background: `linear-gradient(135deg, ${team.primary}, ${team.secondary})` }} />
                  <span className="team-chip-name">{team.name}</span>
                  <span className="team-chip-league">{team.league.toUpperCase()}</span>
                  <button
                    type="button"
                    className="chip-action"
                    onClick={() => setSpearhead(team.id)}
                    disabled={isSpearhead || savingPrefs}
                  >
                    {isSpearhead ? 'Spearhead' : 'Make spearhead'}
                  </button>
                  <button type="button" className="chip-remove" onClick={() => removeTeam(team.id)} disabled={savingPrefs}>
                    Remove
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <div className="league-tabs">
          {LEAGUES.map((league) => (
            <button
              key={league.id}
              type="button"
              className={leagueTab === league.id ? 'is-active' : ''}
              onClick={() => { setLeagueTab(league.id); setTeamQuery(''); }}
            >
              {league.name}
            </button>
          ))}
        </div>

        <label className="profile-label" htmlFor="team-search">Find a {leagueTab.toUpperCase()} team</label>
        <input
          id="team-search"
          value={teamQuery}
          onChange={(e) => setTeamQuery(e.target.value)}
          placeholder={`Search ${leagueTab.toUpperCase()} teams`}
        />
        <ul className="team-search-results">
          {searchResults.map((team) => (
            <li key={team.id}>
              <button type="button" onClick={() => addTeam(team)} disabled={savingPrefs}>
                <span className="team-swatch" style={{ background: `linear-gradient(135deg, ${team.primary}, ${team.secondary})` }} />
                <span>{team.name}</span>
                <span className="add-label">Add</span>
              </button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
};

export default Profile;
